/**
 * SA-1089: the trailing quoted block of an email reply, so the bubble can
 * fold it behind "Show quoted text". Pure and heuristic: it looks for the
 * first line that opens what a mail client appends under a reply, and
 * everything from that line to the end is the quoted block.
 *
 * Markers, in the forms the three common clients write them:
 * - a line ending in `wrote:` (Gmail, Apple Mail: `On …, X <x@y> wrote:`,
 *   possibly wrapped onto two lines, so the marker is the line that ENDS
 *   with it and the attribution starts at the nearest `On ` line above);
 * - an Outlook header block: a `From:` line followed within four lines by
 *   `Sent:` or `Date:`;
 * - SA-1308: the same block in the other languages Compass's mail carries
 *   (German, Spanish, Bulgarian, Russian, Danish/Norwegian, Turkish), with an
 *   address on the from-line, because a route request has the same shape
 *   ("Von: Frankfurt / Nach: Lagos / Datum: 14.10."). All 201 such blocks in
 *   compass-staging's mail carry one (survey, 19 Oct 2026);
 * - the `-----Original Message-----` rule;
 * - a run of `>` lines that reaches the end of the text.
 * SA-1295: a forward's separator line directly above the marker (blank lines
 * between allowed) joins the quoted block: Gmail's `---------- Forwarded
 * message ---------`, Outlook's `________________________________` rule and
 * Apple Mail's `Begin forwarded message:`. A separator alone stays visible.
 * A `>` block in the middle of the text stays visible: the person is
 * answering point by point.
 */

export interface QuoteSplit {
  visible: string
  quoted: string | null
}

const WROTE_LINE = /\bwrote:\s*$/i
const ON_LINE = /^\s*On\s/i
const ORIGINAL_RULE = /^\s*-{2,}\s*Original Message\s*-{2,}\s*$/i
const FROM_LINE = /^\s*From:\s/i
const SENT_OR_DATE = /^\s*(Sent|Date):\s/i
const LOCAL_FROM_LINE = /^\s*(?:Von|De|От|Fra|Kimden):\s.*@/i
const LOCAL_SENT_LINE = /^\s*(?:Gesendet|Datum|Enviado(?: el)?|Изпратено|Отправлено|Дата|Sendt|Tarih):\s/i
const QUOTE_LINE = /^\s*>/
const FORWARD_SEPARATORS = [/^-{2,}\s*forwarded message\s*-{2,}$/i, /^_{10,}$/, /^begin forwarded message:$/i]

function isForwardSeparator(line: string): boolean {
  const trimmed = line.trim()
  return FORWARD_SEPARATORS.some((pattern) => pattern.test(trimmed))
}

function headerBlockAt(lines: string[], i: number): boolean {
  const sent = FROM_LINE.test(lines[i]) ? SENT_OR_DATE : LOCAL_FROM_LINE.test(lines[i]) ? LOCAL_SENT_LINE : null
  if (!sent) return false
  for (let j = i + 1; j < Math.min(lines.length, i + 5); j += 1) {
    if (sent.test(lines[j])) return true
  }
  return false
}

/** The index of the first line the quoted block starts at, or -1. */
function markerIndex(lines: string[]): number {
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    if (WROTE_LINE.test(line)) {
      // The attribution may wrap: start at the nearest `On …` line above, at
      // most two lines up, else at this line.
      let start = i
      for (let j = i; j >= Math.max(0, i - 2); j -= 1) {
        if (ON_LINE.test(lines[j])) { start = j; break }
      }
      return start
    }
    if (ORIGINAL_RULE.test(line) || headerBlockAt(lines, i)) return i
  }
  // A run of `>` lines reaching the end.
  let i = lines.length - 1
  while (i >= 0 && lines[i].trim() === '') i -= 1
  if (i < 0 || !QUOTE_LINE.test(lines[i])) return -1
  let start = i
  while (start - 1 >= 0 && QUOTE_LINE.test(lines[start - 1])) start -= 1
  return start
}

export function splitQuoted(text: string): QuoteSplit {
  if (!text) return { visible: '', quoted: null }
  const lines = text.split('\n')
  let at = markerIndex(lines)
  if (at < 0) return { visible: text, quoted: null }
  let above = at - 1
  while (above >= 0 && lines[above].trim() === '') above -= 1
  if (above >= 0 && isForwardSeparator(lines[above])) at = above
  const visible = lines.slice(0, at).join('\n').replace(/\s+$/, '')
  const quoted = lines.slice(at).join('\n').replace(/\s+$/, '')
  return { visible, quoted: quoted || null }
}
